/**
 * Synthetic, deterministic stand-in for a real password-reset token lookup.
 * Real token generation/validation is explicitly Codex's lane (see
 * docs/ai/CLAUDE_ACCOUNT_RECOVERY_UX_CONTRACT.md) -- this module exists only
 * so ForgotPasswordFlow's "here's what a real email link would do" demo
 * section and ResetPasswordForm's expired/used/invalid states are all
 * reachable and testable without a real backend or a real inbox.
 */
export type RecoveryTokenStatus = 'valid' | 'expired' | 'used' | 'invalid';

export const DEMO_RECOVERY_TOKENS: Record<string, RecoveryTokenStatus> = {
  'demo-valid-token': 'valid',
  'demo-expired-token': 'expired',
  'demo-used-token': 'used',
};

export function lookupRecoveryToken(token: string | null | undefined): RecoveryTokenStatus {
  if (!token) return 'invalid';
  return DEMO_RECOVERY_TOKENS[token] ?? 'invalid';
}

export type PasswordRequirement = {id: string; label: string; test: (password: string) => boolean};

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {id: 'length', label: 'At least 14 characters', test: (password) => password.length >= 14},
  {id: 'upper', label: 'One uppercase letter', test: (password) => /[A-Z]/.test(password)},
  {id: 'lower', label: 'One lowercase letter', test: (password) => /[a-z]/.test(password)},
  {id: 'digit', label: 'One number', test: (password) => /\d/.test(password)},
  {id: 'symbol', label: 'One symbol (e.g. ! ? # %)', test: (password) => /[^A-Za-z0-9]/.test(password)},
];

export type PasswordStrength = 'too_weak' | 'fair' | 'strong';

export function passwordStrength(password: string): PasswordStrength {
  if (!password) return 'too_weak';
  const metCount = PASSWORD_REQUIREMENTS.filter((requirement) => requirement.test(password)).length;
  if (metCount >= 5) return 'strong';
  if (metCount >= 3) return 'fair';
  return 'too_weak';
}

export function passwordMeetsAllRequirements(password: string): boolean {
  return PASSWORD_REQUIREMENTS.every((requirement) => requirement.test(password));
}
